/**
 * txDescription.js, Client-side prettifier for raw statement-import
 * descriptions (Indian bank statements primarily).
 *
 * Statement PDFs use highly encoded descriptors like:
 *   UPI-COSMOS TAILORS-POS.11309942@INDUS-INDB0MERCHA-...-PAYMENT TO POS 113
 *   IMPS-615730182924-KOMALAM-SBIN-XXXXXXX5613-IMPS TRANSACTION
 *   NEFT DR-INDB0000091-ASHISH MANI KURIAKOS E-NETBANK, MUM-HDFC...
 *   ATW-526099XXXXXX3344-S1ACPL07-ERNAKULAM
 *
 * Users want to see a clean merchant/person name + an at-a-glance payment-
 * mode badge. We deliberately keep the parser purely heuristic and SAFE:
 * if a pattern doesn't match cleanly, we fall back to the raw string  
 * never fabricate.
 *
 * Returns:
 *   { display, mode, raw }
 *     display , pretty short string (title-cased counterparty / location)
 *     mode    , one of: 'UPI' | 'IMPS' | 'NEFT' | 'RTGS' | 'ATM' |
 *                'POS' | 'Card' | 'ACH' | 'ECS' | 'Cash' | null | 'merchant'
 *     raw     , original description (always preserved for tooltips)
 *
 * Phase 70B — Canonical merchant resolver layered on top. See
 * `lib/merchantNames.js`. When a raw string contains a known brand
 * (~150 seeded), we short-circuit and return the brand name with
 * mode='merchant'. The Indian-bank parsers below remain the fallback.
 */

import { resolveMerchantNameRich } from './merchantNames';


const STOP_WORDS = new Set([
  // Generic financial verbs / nouns
  'PAYMENT', 'PAYMENTS', 'TO', 'FROM', 'TXN', 'TRANSACTION', 'TRANSFER',
  'BANK', 'NETBANK', 'MERCHANT', 'MERCHA', 'POS', 'COLLECT', 'TRF',
  // Rail / mode identifiers (we surface these as the badge separately)
  'IMPS', 'NEFT', 'RTGS', 'UPI', 'ATW', 'ATM', 'ACH', 'ECS',
  // Direction / type tags (HDFC, ICICI, Yes Bank UPI strings include these
  // between the reference and the merchant tag, e.g.
  // "UPI/614732524815/DR/ANNS/YESB/..." — they're noise, not the merchant)
  'DR', 'CR', 'INW', 'OUT', 'PAY', 'P2A', 'P2P', 'P2M',
  // Common UPI VPA suffixes (the bit after the @ in handles like
  // "merchant@ybl"). These are PSP names, never merchant names.
  'YBL', 'OKSBI', 'OKHDFCBANK', 'OKICICI', 'OKAXIS',
  'PAYTM', 'PTYS', 'PTM', 'PAYZAPP',
  'RAPL', 'FBL', 'AXB', 'AXL', 'DBL', 'IBL', 'HDFCBANK', 'ICICIBANK',
  'AXISBANK', 'SBI', 'IDFCBANK', 'KKBK', 'SBIN', 'YESB', 'UTIB', 'INDB',
  'IDBI', 'PNB', 'CITI', 'HSBC', 'BARB', 'CNRB', 'CBIN', 'BKID', 'BOFA',
  'FDRL', 'RATN', 'IOBA', 'UCBA',
  // Two-letter / very short tail tags that some banks append
  'AD', 'AD04', 'INV', 'EMI', 'NRI',
]);

const ALLCAPS_KEEP = new Set([
  'UPI', 'NEFT', 'IMPS', 'RTGS', 'ACH', 'ATM', 'POS', 'EMI', 'NRI', 'KYC',
  'GST', 'IGST', 'CGST', 'SGST', 'TDS', 'PF', 'ESI', 'HDFC', 'SBI', 'ICICI',
  'IDFC', 'YES', 'IDBI', 'AXIS', 'PNB', 'BOB', 'BOI', 'IOB', 'UCO', 'IND',
  'INR', 'USD', 'EUR', 'GBP', 'AED', 'CAD', 'AUD',
]);

function titleCaseWord(w) {
  if (!w) return '';
  // Preserve known acronyms/codes in ALL CAPS
  const upper = w.toUpperCase();
  if (ALLCAPS_KEEP.has(upper)) return upper;
  // Don't touch short tokens that look like bank codes (e.g. "Q256")
  if (/^[A-Z0-9]{4,}$/.test(w) && /\d/.test(w)) return w;
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}

function titleCase(str) {
  if (!str) return '';
  return str
    .replace(/[_]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(titleCaseWord)
    .join(' ')
    .trim();
}

// Strip trailing noise like "E", "MUM" (single letters / city abbreviations
// that get appended by banks), we only strip them when they sit at the END
// to keep the visible name short.
function tidyName(s) {
  if (!s) return '';
  let out = s.trim();
  // Strip a trailing literal "..." that some banks leave when a narration
  // is hard-truncated, before doing the alpha cleanup below.
  out = out.replace(/\s*\.{3,}\s*$/, '');
  // Phase 55 follow-up — also handle truncation artifacts like "KURIAK A"
  // where the last name got cut at a word boundary by the bank. Strip up
  // to 2 dangling single letters at the end before the comma cleanup.
  out = out.replace(/(\s+[A-Z]){1,2}\s*$/, '');
  out = out.replace(/[,;]+$/, '');
  return out.trim();
}

function isMostlyDigits(s) {
  if (!s) return false;
  const digits = (s.match(/\d/g) || []).length;
  return digits >= s.length * 0.6;
}

function pickFirstMeaningfulSegment(segments) {
  // Walk segments and return the first one that isn't a pure number/handle.
  for (const seg of segments) {
    const t = (seg || '').trim();
    if (!t) continue;
    if (isMostlyDigits(t)) continue;
    if (/@/.test(t)) continue; // VPA handles like xxx@ybl
    if (STOP_WORDS.has(t.toUpperCase())) continue;
    return t;
  }
  return '';
}

export function prettifyDescription(raw) {
  if (raw === null || raw === undefined) return { display: '', mode: null, raw: '' };
  const original = String(raw).trim();
  if (!original) return { display: '', mode: null, raw: '' };

  // Phase 70B — Canonical merchant resolver runs FIRST.
  //
  // The seed list in `lib/merchantNames.js` matches ~150 well-known
  // brands (Netflix, Swiggy, Whole Foods, Vanguard, …) against a
  // lowercase + alphanumeric-only normalisation of the raw string. If
  // we hit one (`hit: true`), we surface the brand name immediately
  // and stop. The fallback path (`hit: false`) returns a cleaned-up
  // version of the input but we deliberately IGNORE it here and let
  // the Indian-bank-shaped parsers below take over instead — they
  // know more about UPI/IMPS/NEFT formats than the generic fallback.
  //
  // Display-only. The raw string is returned untouched so search,
  // edit forms, and category rules continue to work as before.
  try {
    const canon = resolveMerchantNameRich(original);
    if (canon && canon.hit && canon.display) {
      return { display: canon.display, mode: 'merchant', raw: original };
    }
  } catch {
    // Fall through to legacy parser on any unexpected import error.
  }

  // ---------------------------------------------------------------------
  // Pre-clean: many Indian bank statement narrations are reproduced with
  // surrounding noise that the parser couldn't fully strip, e.g.
  //   "31/05/26 Upi-hafsal P S-...-payment To Hafsalk 0000... 31/05/26 500"
  //   "31/05/26 APRAPR26 Instaalertchg 5 Sms-epr27..."
  // Strip leading date prefixes (DD/MM/YY or DD-MM-YYYY) and any
  // trailing date/amount tail so the pattern matchers below can fire.
  // ---------------------------------------------------------------------
  let working = original;

  // Repeatedly strip leading date-shaped tokens. We loop because some rows
  // have a date repeated twice (e.g. "31/05/26 31/05/26 Upi-...").
  for (let i = 0; i < 3; i++) {
    const stripped = working.replace(
      /^\s*\d{1,2}[\s\-/.]\d{1,2}[\s\-/.]\d{2,4}\s+/,
      ''
    );
    if (stripped === working) break;
    working = stripped;
  }

  // Strip a trailing "<date> <amount>" tail that some HDFC/SBI exports
  // duplicate at the end of the description.
  working = working.replace(
    /\s+\d{1,2}[\s\-/.]\d{1,2}[\s\-/.]\d{2,4}(\s+[\d,\.]+){0,3}\s*$/,
    ''
  );
  working = working.trim();

  // ---------------------------------------------------------------------
  // SMS alert / bank service charges. Some HDFC narrations look like
  //   "APRAPR26 Instaalertchg 5 Sms-epr2715085736265 EPR2715085736265"
  // There's no real merchant here, it's a recurring bank fee. Surface it
  // as a friendly fixed label so the list doesn't read like noise.
  // ---------------------------------------------------------------------
  if (/Instaalert|Insta\s*Alert|Sms[\s\-]?Alert|Sms[\s\-]?Chg|Sms\s+Charge|Alert\s*Chg/i.test(working)) {
    return { display: 'SMS alert charge', mode: null, raw: original };
  }
  if (/^Annual\s*Maint|Amc\b|Locker\s*Rent|Cheque\s*Book\s*Chg|Atm\s*Chg/i.test(working)) {
    return { display: 'Bank service charge', mode: null, raw: original };
  }

  // ---------------------------------------------------------------------
  // IMPS transaction charges (Phase 84.2.3)
  // Bank statements often carry IMPS fee rows whose entire narration is
  // an EPR (Eligible Product Reference) code repeated once or twice with
  // no merchant name attached:
  //   "EPR2715085736265 EPR2715085736265"
  //   "IMPS-EPR2715085736265-EPR2715085736265-IMPS CHARGE"
  //   "Epr Epr"  (already tokenised by an upstream parser)
  // After stripping the EPR/IMPS/digit tokens, there's nothing left to
  // read. Surface a friendly fixed label instead of leaking bank codes.
  // ---------------------------------------------------------------------
  {
    const containsEpr = /EPR\d*/i.test(working);
    if (containsEpr) {
      const residue = working
        .replace(/EPR[\s\-]?\d*/gi, ' ')
        .replace(/\b(IMPS|NEFT|RTGS|CHG|CHARGE|CHARGES|FEE|FEES|GST|TXN|TRANSACTION|TRF|TRANSFER)\b/gi, ' ')
        .replace(/[\s\-/,.]+/g, ' ')
        .replace(/\d{4,}/g, ' ')
        .trim();
      if (!residue || residue.length < 3) {
        return { display: 'IMPS charge', mode: 'IMPS', raw: original };
      }
    }
  }

  // Normalise separators (some statements use '/' instead of '-')
  const s = working;

  // ---------------------------------------------------------------------
  // UPI aggregator VPAs (PayU / Razorpay / Paytm / PhonePe etc.)
  //   Phase 84.2.3
  // Some banks (ICICI, Federal, IDFC) truncate the "UPI-" prefix and
  // just leave the raw UTR + VPA suffix in the narration, e.g.
  //   "K4uh254ma572bnouce/payurajagirichristuj"
  //   "398675432101/razpspicejet2025x"
  //   "512334987654/paytmswiggy@paytm"
  // The `payu<merchant>` / `razp<merchant>` fragment is a well-known
  // aggregator VPA — everything after the aggregator prefix (minus
  // trailing @handle / dangling letters) is the actual merchant name.
  // Recognise this BEFORE the generic UPI branch below, because these
  // narrations don't start with "UPI-" and would otherwise fall to the
  // gibberish fallback.
  // ---------------------------------------------------------------------
  {
    const aggregatorVpa = s.match(
      /(?:^|[\s\-/])(payu|razorpay|razp|rzp|paytm|phonepe|ppe|bharatpe|bp|googlepay|gpay|amazonpay|apay|instamojo|billdesk|ccavenue)([a-z][a-z0-9]{2,40})/i
    );
    if (aggregatorVpa) {
      const agg = aggregatorVpa[1];
      let merchantSlug = aggregatorVpa[2] || '';
      // Some banks append a single trailing letter (routing suffix) that
      // isn't part of the merchant name. Strip an isolated trailing
      // lowercase letter when the slug is long enough that losing one
      // character doesn't harm readability.
      merchantSlug = merchantSlug.split('@')[0];
      if (merchantSlug.length > 6 && /[a-z]$/.test(merchantSlug)) {
        // Only strip if it looks like a routing suffix (not part of a word).
        // Common tail letters seen in real captures: 'j', 'x', 'k', 'z'.
        if (/[jxkzq]$/i.test(merchantSlug)) {
          merchantSlug = merchantSlug.slice(0, -1);
        }
      }
      // Insert spaces before capital letters in case the slug is camelCase,
      // e.g. "rajagiriChristu" -> "rajagiri Christu".
      const spaced = merchantSlug.replace(/([a-z])([A-Z])/g, '$1 $2');
      const display = titleCase(tidyName(spaced));
      if (display && display.length >= 3 && !STOP_WORDS.has(display.toUpperCase())) {
        const aggLabel = {
          payu: 'PayU', razorpay: 'Razorpay', razp: 'Razorpay', rzp: 'Razorpay',
          paytm: 'Paytm', phonepe: 'PhonePe', ppe: 'PhonePe',
          bharatpe: 'BharatPe', bp: 'BharatPe',
          googlepay: 'GPay', gpay: 'GPay',
          amazonpay: 'Amazon Pay', apay: 'Amazon Pay',
          instamojo: 'Instamojo', billdesk: 'BillDesk', ccavenue: 'CCAvenue',
        }[agg.toLowerCase()] || 'UPI';
        return { display: `${display} \u00b7 ${aggLabel}`, mode: 'UPI', raw: original };
      }
    }
  }

  // --- DEP TFR / Bank deposit-transfer with named counterparty ----------
  // Examples (typical State Bank of India / regional banks):
  //   "DEP TFR For personal use 0040164771047 OF Mrs. ALEYAMMA KURIAKO AT 0948..."
  //   "DEP TFR CLOSURE BY CUST REQ 0020010218417 OF Mr. ASHISH MANI KURIAK A..."
  //   "DEP TFR ... OF Dr. PRIYA NAIR AT ..."
  // The cleanest signal is the "OF Mr./Mrs./Ms./Dr. <NAME>" fragment.
  // Everything after `OF <Title>` up to `AT <branch>` (or end of string)
  // is the counterparty name. Title is preserved for warmth ("Mrs. Aleyamma
  // Kuriako" reads like a person, not a noun).
  {
    const m = s.match(
      /\b(?:DEP\s*TFR|DEPOSIT\s*TFR|DEP\s*TRANSFER)\b[\s\S]*?\bOF\s+((?:Mr|Mrs|Ms|Miss|Dr|Mx)\.?\s+)?([A-Z][A-Za-z][A-Za-z .'-]{1,60}?)(?=\s+(?:AT|REF|REFNO|UTR|NEFT|IMPS|RTGS)\b|\s+\d|\s*$)/i,
    );
    if (m) {
      const honorific = (m[1] || '').trim();
      const rawName = (m[2] || '').trim();
      const name = titleCase(tidyName(rawName));
      if (name && name.length >= 2) {
        const display = honorific
          ? `${honorific.replace(/\.?$/, '.')}\u00a0${name}`
          : name;
        return { display, mode: null, raw: original };
      }
    }
  }

  // --- N/ prefix (HDFC NEFT outgoing transfer format) ----------------
  // Example: "N/HDFCH01037306077/HDFC0000001/ASHISH MANI KURIAKO"
  //   parts = ['N', 'HDFCH01037306077', 'HDFC0000001', 'ASHISH MANI KURIAKO']
  //   The last segment is the counterparty name.
  if (/^N[\s\-/][A-Z0-9]{6,}/.test(s)) {
    const parts = s.split(/[-/]/).filter(Boolean);
    // Walk from the right to find a name-shaped token (mostly letters)
    let name = '';
    for (let i = parts.length - 1; i >= 0; i--) {
      const t = (parts[i] || '').trim();
      if (!t) continue;
      if (/^[A-Z]{3,5}\d{4,}$/.test(t)) continue; // bank code
      if (isMostlyDigits(t)) continue;
      if (STOP_WORDS.has(t.toUpperCase())) continue;
      name = t;
      break;
    }
    if (name) {
      return {
        display: titleCase(tidyName(name)) || 'Bank Transfer',
        mode: 'NEFT',
        raw: original,
      };
    }
  }

  // --- UPI ---------------------------------------------------------------
  // UPI-{NAME or BUSINESS}-{vpa}@{handle}-{bank}-{ref}-PAYMENT TO {x}
  if (/^UPI[\s\-/]/i.test(s)) {
    const parts = s.split(/[-/]/).slice(1); // drop "UPI"
    let name = pickFirstMeaningfulSegment(parts);
    if (!name) name = parts[0] || '';
    // The first segment may carry a trailing dangling-letter remnant
    // (e.g. "hafsal P S" from a name shape like "Hafsal P. S.") or be
    // suffixed with an @vpa. Trim both.
    name = name.replace(/\s+[A-Z]\s+[A-Z]\s*$/, '').trim();
    name = name.split('@')[0].trim();

    // If the segment-walker only found a tiny / numeric / VPA-shaped token,
    // fall back to "payment To <Merchant>" which most HDFC narrations end
    // with. We do this AS A FALLBACK (not the primary) because the first
    // meaningful segment is usually the cleaner of the two.
    const tooShort = !name || name.length < 3 || /^[\d\s]+$/.test(name);
    if (tooShort) {
      const payTo = s.match(/payment\s+To\s+([A-Za-z][A-Za-z0-9 ._'&-]{1,40}?)(?=\s+\d|\s*$)/i);
      if (payTo && payTo[1]) {
        const cleaned = tidyName(payTo[1].trim());
        const candidate = titleCase(cleaned);
        if (candidate && candidate.length >= 2 && !STOP_WORDS.has(candidate.toUpperCase())) {
          return { display: candidate, mode: 'UPI', raw: original };
        }
      }
    }

    return {
      display: titleCase(tidyName(name)) || 'UPI Payment',
      mode: 'UPI',
      raw: original,
    };
  }

  // --- IMPS --------------------------------------------------------------
  // IMPS-{ref}-{NAME}-{bank}-{masked_acct}-IMPS TRANSACTION
  if (/^IMPS[\s\-/]/i.test(s)) {
    const parts = s.split(/[-/]/).slice(1);
    const name = pickFirstMeaningfulSegment(parts);
    return {
      display: titleCase(tidyName(name)) || 'IMPS Transfer',
      mode: 'IMPS',
      raw: original,
    };
  }

  // --- NEFT (DR / CR) ----------------------------------------------------
  // NEFT DR-{ref}-{NAME desc}-{codes}
  // NEFT CR-{ref}-{NAME desc}-{codes}
  const neft = s.match(/^NEFT\s*(DR|CR)?[\s\-/](.*)$/i);
  if (neft) {
    const rest = neft[2] || '';
    const parts = rest.split(/[-/]/);
    let name = pickFirstMeaningfulSegment(parts);
    // Take only the first comma/period segment so we don't show 'X, MUM'
    name = (name || '').split(/[,.]/, 1)[0].trim();
    return {
      display: titleCase(tidyName(name)) || 'NEFT Transfer',
      mode: 'NEFT',
      raw: original,
    };
  }

  // --- RTGS --------------------------------------------------------------
  if (/^RTGS[\s\-/]/i.test(s)) {
    const parts = s.split(/[-/]/).slice(1);
    const name = pickFirstMeaningfulSegment(parts);
    return {
      display: titleCase(tidyName(name || '').split(/[,.]/, 1)[0]) || 'RTGS Transfer',
      mode: 'RTGS',
      raw: original,
    };
  }

  // --- ATM withdrawal ----------------------------------------------------
  // ATW-{masked_card}-{atm_id}-{LOCATION}
  if (/^ATW[\s\-/]/i.test(s) || /^ATM\s/i.test(s)) {
    const parts = s.split(/[-/]/);
    // Take the LAST non-numeric segment as the location
    let loc = '';
    for (let i = parts.length - 1; i >= 0; i--) {
      const t = (parts[i] || '').trim();
      if (!t) continue;
      if (isMostlyDigits(t)) continue;
      if (/^ATW$/i.test(t) || /^ATM$/i.test(t)) continue;
      loc = t;
      break;
    }
    const pretty = titleCase(tidyName(loc));
    return {
      display: pretty ? `ATM \u2022 ${pretty}` : 'ATM Withdrawal',
      mode: 'ATM',
      raw: original,
    };
  }

  // --- POS / Card transaction -------------------------------------------
  if (/^POS[\s\-/]/i.test(s) || /\bPOS\s*\d/i.test(s)) {
    const parts = s.split(/[-/]/);
    let name = '';
    // POS transactions often have the merchant after the POS code
    for (const p of parts) {
      const t = (p || '').trim();
      if (!t) continue;
      if (/^POS/i.test(t)) continue;
      if (isMostlyDigits(t)) continue;
      name = t;
      break;
    }
    return {
      display: titleCase(tidyName(name)) || 'Card Payment',
      mode: 'POS',
      raw: original,
    };
  }

  // --- ACH / ECS (auto-debits, mandates) --------------------------------
  if (/^ACH[\s\-/]/i.test(s)) {
    const parts = s.split(/[-/]/).slice(1);
    const name = pickFirstMeaningfulSegment(parts);
    return {
      display: titleCase(tidyName(name)) || 'Auto-debit',
      mode: 'ACH',
      raw: original,
    };
  }
  if (/^ECS[\s\-/]/i.test(s)) {
    const parts = s.split(/[-/]/).slice(1);
    const name = pickFirstMeaningfulSegment(parts);
    return {
      display: titleCase(tidyName(name)) || 'ECS Debit',
      mode: 'ECS',
      raw: original,
    };
  }

  // --- Cash deposit/withdrawal at branch --------------------------------
  if (/^(CASH\s+(DEP|WDL|WITHDRAWAL|DEPOSIT))/i.test(s)) {
    return { display: titleCase(s), mode: 'Cash', raw: original };
  }

  // --- Fallback ----------------------------------------------------------
  // Long all-caps strings → softly title-case to reduce shouting.
  if (s.length > 12 && /[A-Z]/.test(s) && !/[a-z]/.test(s)) {
    return { display: titleCase(s), mode: null, raw: original };
  }
  return { display: s, mode: null, raw: original };
}

/** Short label shown in the badge. Some modes get nicer copy. */
export function modeLabel(mode) {
  if (!mode) return '';
  if (mode === 'POS') return 'Card';
  return mode;
}


// ---------------------------------------------------------------------------
// suggestPatternFromDescription, pick a STABLE token from the raw
// description that will recur across multiple transactions from the same
// vendor. Used by the import "Remember this for next time?" dialog so that
// "Also apply to other matching rows in this batch" actually finds matches.
//
// Examples (HDFC UPI format):
//   UPI/614732524815/DR/ANNS/YESB/m-82896371@ptys     -> "ANNS"
//   UPI/614158710308/DR/Emer/AIRP/26.rzp@rxairtel      -> "Emer"
//   UPI/650861174608/DR/Amaz/RATN/amazon@rapl          -> "Amaz"
//   UPI/650298099945/DR/ERNA/FDRL/mcmaincash1@fbl      -> "ERNA"
//   IMPS/P2A/613719921571/SBIN/ASHISH MANI KURIAKOSE   -> "ASHISH MANI KURIAKOSE"
//   ACH DR INW PAY/0000165298022/ HDFC BANK LIMITED    -> "HDFC BANK LIMITED"
//   N/HDFCH01037306077/HDFC0000001/ASHISH MANI KURIAKO -> "ASHISH MANI KURIAKO"
//
// Strategy:
//   1) If prettifier returns a non-trivial display, use that as the pattern
//      (it's already merchant/person-shaped).
//   2) Otherwise, fall back to first non-numeric, non-stopword token of
//      length >= 3.
// ---------------------------------------------------------------------------
function looksLikeBankMaskCode(token) {
  // e.g. INDB0000091, HDFCH01037306077, HDFC0000001, bank code prefixed with
  // 3-5 letters followed by lots of digits. Useless as merchant identifier.
  return /^[A-Z]{3,5}\d{4,}$/.test(token);
}

function looksLikeMaskedAccount(token) {
  // e.g. XXXXXXX5613, 526099XXXXXX3344, card / account masks
  return /[X]{3,}/i.test(token);
}

export function suggestPatternFromDescription(raw) {
  if (!raw) return '';
  const original = String(raw).trim();
  if (!original) return '';

  // Hotfix (2026-05-21): normalize output so patterns stored in
  // merchant_rules never carry the fixed-width whitespace padding some
  // bank statements produce (e.g. "Noon Food               DUBAI").
  // Without this, downstream substring matchers silently fail against
  // rows whose whitespace is even slightly different.
  const normalizePattern = (s) => (s || '').replace(/\s+/g, ' ').trim();

  // Try the prettifier first, it already extracts merchant/person names.
  const pretty = prettifyDescription(original);
  if (pretty.display) {
    // Strip the "ATM \u2022 " prefix we add for ATM rows, the user wants the
    // location as the pattern, not the prefix.
    const cleaned = normalizePattern(pretty.display.replace(/^ATM\s*\u2022\s*/i, ''));
    if (cleaned && cleaned.length >= 3 && !/^[\d\s]+$/.test(cleaned)) {
      return cleaned;
    }
  }

  // Fallback: walk tokens and pick first that looks like a real word.
  const tokens = original.split(/[^A-Za-z0-9@]+/).filter(Boolean);
  for (const t of tokens) {
    if (t.length < 3) continue;
    if (/^\d+$/.test(t)) continue;                  // pure numeric
    if (STOP_WORDS.has(t.toUpperCase())) continue;
    if (looksLikeBankMaskCode(t)) continue;
    if (looksLikeMaskedAccount(t)) continue;
    if (/@/.test(t)) continue;                       // VPA handle
    return normalizePattern(t);
  }

  // Last-resort: first 12 chars of the original (preserves legacy behaviour).
  return normalizePattern(original.slice(0, 12));
}
